import { Component } from '@theme/component';
import { ProductComponent } from '@theme/view-event-elements';
import VariantPicker from '@theme/variant-picker';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { PrefetchCache } from '@theme/section-renderer';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends ProductComponent {
  requiredRefs = ['productCardLink'];

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

  }

  /**
   * Handles quick add functionality from variant swatches.
   * @param {Event} event - Click event
   */
  quickAdd = (event) => {
    event.preventDefault();
    if (this.quickViewOverlay && this.refs?.quickView) {
      this.refs.quickView.handleClick(event);
    } else if (this.quickAddOverlay && this.refs?.quickAdd) {
      this.refs.quickAdd.handleClick(event);
    } else {
      location.href = this.refs.productCardLink;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }


   /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (!this.isConnected) return;
    event.stopPropagation();
    const html = event.detail.data.html.querySelector('product-card');
    const productCardElement = /** @type {Element} */ (html.cloneNode(true));
    this.#updateVariantImages(productCardElement);
    this.#updateVariantBadges(productCardElement);
    this.#updateVariantPrice(productCardElement);
    this.#updateProductUrl(productCardElement);
    this.#updateProductCardClasses(productCardElement);
    this.updateBackInStockButton(productCardElement);
  };

   /**
   * Updates the DOM with a new badges.
   * @param {Element} productCardElement - The product card element.
   */
   #updateVariantImages(productCardElement) {
    const productMediaElement = this.querySelector('.product-card__media');
    const newProductMediaElement = productCardElement.querySelector('.product-card__media');
    const productVideoElement = productMediaElement.querySelector('deferred-media');
    if (productVideoElement) {
      const primaryImageElement = productVideoElement.querySelector('.deferred-media__poster-image');
      const newSecondaryImageElement = newProductMediaElement.querySelector('.product-media--secondary-image');
      const newPrimaryImageElement = newProductMediaElement.querySelector('.product-media--primary-image');
      const primayImageSizes = primaryImageElement ? primaryImageElement.sizes : '';
      if (newSecondaryImageElement && primayImageSizes) {
        newSecondaryImageElement.setAttribute('sizes', primayImageSizes);
      }
      if (newPrimaryImageElement && primayImageSizes) {
        newPrimaryImageElement.setAttribute('sizes', primayImageSizes);
      }
    }

    if (newProductMediaElement && productMediaElement) {
      morph(productMediaElement, newProductMediaElement);
    }
  }

   /**
   * Updates the DOM with a new badges.
   * @param {Element} productCardElement - The product card element.
   */
   #updateVariantBadges(productCardElement) {
    const badgesContainer = this.querySelector('.product-card__badges');
    const newBadgesElement = productCardElement.querySelector('.product-card__badges');

    if (newBadgesElement && badgesContainer) {
      morph(badgesContainer, newBadgesElement);
    }
  }

    /**
   * Updates the DOM with a new price.
   * @param {Element} productCardElement - The product card element.
   */
  #updateVariantPrice(productCardElement) {
    const priceContainer = this.querySelector('.product-card__price');
    const newPriceElement = productCardElement.querySelector('.product-card__price');

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the DOM with a new product card classes.
   * @param {Element} productCardElement - The product card element.
   */
  #updateProductCardClasses(productCardElement) {
    const isBackInStock = productCardElement.getAttribute('data-back-in-stock') === 'true';
    if (isBackInStock) {
      this.classList.add('show-back-in-stock');
    } else {
      this.classList.remove('show-back-in-stock');
    }
  }

  /**
   * Updates the DOM with a new back-in-stock button.
   * @param {Element} productCardElement - The product card element.
   */
  updateBackInStockButton(productCardElement) {
    const anchorElement = productCardElement?.querySelector('product-card a');
    if (anchorElement instanceof HTMLAnchorElement) {
      const productUrl = anchorElement.getAttribute('href');
      if (!productUrl) return;
      const currentBackInStockButton = this.querySelector('back-in-stock-component');
      if (currentBackInStockButton) {
        currentBackInStockButton.setAttribute('data-product-url', productUrl);
      }
    }
  }

 /**
   * Updates the product URL based on the variant update event.
   * @param {Element} productCardElement - The product card element.
   */
  #updateProductUrl(productCardElement) {
    const anchorElement = productCardElement?.querySelector('product-card a');

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      let productUrl = anchorElement.getAttribute('href');
      if (!this.productLinks) return;

      this.productLinks.forEach(link => {
        const collection = link.getAttribute('collection');
        if (collection) {
          productUrl = collection + productUrl;
        }
        link.href = productUrl;
      });
    }
  }

  /**
   * Gets product media component.
   * @returns {ProductMedia | null} Product media component
   */
  get productMedia() {
    return this.querySelector('.product-card__image');
  }

  /**
   * Gets all product card links.
   * @returns {NodeList} Product card links
   */
  get productLinks() {
    return this.querySelectorAll('.product-card__link');
  }

  /**
   * Gets variant swatches element.
   * @returns {HTMLElement | null} Variant swatches element
   */
  get variantSwatches() {
    return this.querySelector('.product-card__swatches');
  }

  /**
   * Gets primary media element.
   * @returns {HTMLElement | null} Primary media element
   */
  get primaryMedia() {
    return this.querySelector('.product-media--primary-image');
  }

  /**
   * Gets secondary media element.
   * @returns {HTMLElement | null} Secondary media element
   */
  get secondaryMedia() {
    return this.querySelector('.product-media--secondary-image');
  }

  /**
   * Gets swatches variant picker element.
   * @returns {HTMLElement | undefined} Swatches variant picker element
   */
  get swatchesVariantPicker() {
    return this.refs.swatchesVariantPicker;
  }

  /**
   * Gets second image hover setting.
   * @returns {boolean} Whether second image hover is enabled
   */
  get secondImageHover() {
    return this.dataset.secondImageHover === 'true';
  }

  /**
   * Gets product card link element.
   * @returns {HTMLAnchorElement} Product card link
   */
  getProductCardLink() {
    return this.refs.productCardLink;
  }

  get quickAddOverlay() {
    return document.querySelector('.shopify-section-overlay-quick-add');
  }

  get quickViewOverlay() {
    return document.querySelector('.shopify-section-overlay-quick-view');
  }

}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /** @type {PrefetchCache<Document>} */
  #cache = new PrefetchCache({
    fetcher: (url, signal) => this.#fetchDoc(url, signal),
    maxSize: SwatchesVariantPickerComponent.MAX_CACHE_SIZE,
    shouldCache: () => !Shopify.designMode,
  });
  /** @type {boolean} */
  #isClickFetching = false;
  /** @type {number | null} */
  #hoverTimeout = null;
  /** @type {AbortController | null} - For fetchUpdatedSection (full re-fetch fallback). */
  #fetchController = null;

  // Constants for timing
  static HOVER_DELAY = 150;
  static MAX_CACHE_SIZE = 5;

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.boundHandleCardVariantUrlUpdate = this.#handleCardVariantUrlUpdate.bind(this);
    this.addEventListener(ThemeEvents.variantUpdate, this.boundHandleCardVariantUrlUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.boundHandleCardVariantUrlUpdate) {
      this.removeEventListener(ThemeEvents.variantUpdate, this.boundHandleCardVariantUrlUpdate);
    }

    this.#clearHoverTimeout();
    this.#fetchController?.abort();
    this.#fetchController = null;
    this.#cache.abort();
    this.#cache.clear();
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (!this.pendingVariantId || !(this.parentProductCard instanceof ProductCard)) return;

    const { refs } = this.parentProductCard;
    const variantId = this.pendingVariantId;

    // Update product card link href
    const productUrl = new URL(refs.productCardLink.href);
    productUrl.searchParams.set('variant', variantId);
    refs.productCardLink.href = productUrl.toString();

    // Update quick add and quick view data-product-link attributes
    const urlUpdates = [
      { element: refs.quickAdd, attr: 'data-product-link' },
      { element: refs.quickView, attr: 'data-product-link' }
    ];

    urlUpdates.forEach(({ element, attr }) => {
      if (!element) return;
      const url = new URL(element.getAttribute(attr), window.location.origin);
      url.searchParams.set('variant', variantId);
      element.setAttribute(attr, url.toString());
    });

    this.pendingVariantId = null;
  }

  /**
   * Build request URL for variant.
   * @param {string} variantId - Variant ID
   * @returns {string | null} Request URL or null if invalid
   */
  #buildVariantRequestUrl(variantId) {
    if (!variantId) return null;

    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return null;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'section-rendering-product-card');

    return url.href;
  }

  /**
   * Handle hover event to preload variant data.
   * @param {Event} event - Pointer enter event
   */
  variantLoaded(event) {
    event.preventDefault();
    event.stopPropagation();

    const swatchInput = event.target.querySelector('input[type="radio"]');
    if (!(swatchInput instanceof HTMLInputElement)) return;

    const variantId = swatchInput.dataset.firstAvailableOrFirstVariantId || swatchInput.dataset.variantId;
    const requestUrl = this.#buildVariantRequestUrl(variantId);
    if (!requestUrl) return;

    // Clear any existing timeout
    this.#clearHoverTimeout();

    // Add small delay to avoid fetch on accidental hover
    this.#hoverTimeout = setTimeout(() => {
      // Don't let a trailing hover prefetch abort an in-flight click fetch.
      if (this.#isClickFetching) return;
      this.#cache.prefetch(requestUrl);
    }, SwatchesVariantPickerComponent.HOVER_DELAY);
  }

  /**
   * Clear hover timeout.
   */
  #clearHoverTimeout() {
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
  }

  /**
   * Fetcher for the prefetch cache: fetch a variant URL and parse it into a
   * Document. Any error (including abort) is surfaced as `null`.
   * @param {string} url - URL to fetch
   * @param {AbortSignal} [signal] - Abort signal from the cache
   * @returns {Promise<Document | null>}
   */
  async #fetchDoc(url, signal) {
    try {
      const response = await fetch(url, { signal });
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch {
      return null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  async variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Handle unavailable swatch variants: show first available variant instead
    if (isSwatchInput && firstAvailableVariantId) {
      // User clicked unavailable variant, show available alternative
      event.stopPropagation();

      // Build URL for available variant
      const requestUrl = this.#buildVariantRequestUrl(firstAvailableVariantId);
      if (!requestUrl) return;

      // Store for URL update after morph
      this.pendingVariantId = firstAvailableVariantId;

      // Resolve via the shared cache: cache hit → in-flight hover prefetch →
      // fresh fetch. #isClickFetching blocks a trailing hover prefetch from
      // aborting this fetch.
      const selection = this.announceProductSelect();

      this.#isClickFetching = true;
      let html;
      try {
        html = await this.#cache.get(requestUrl);
      } finally {
        this.#isClickFetching = false;
      }

      // Update section from cache or fetched content
      if (html) {
        // Cards carry no variant JSON, but the id is the one this click resolved to.
        selection.resolve({ variantId: String(firstAvailableVariantId) });
        this.#updateSectionFromCache(html);
      } else {
        // Fallback if cache fails — fetchUpdatedSection announces its own attempt
        selection.reject(new Error('Variant section could not be loaded'));
        this.fetchUpdatedSection(requestUrl);
      }

      return;
    }
  }

  /**
   * Update section from cached HTML.
   * @param {Document} html - Cached HTML document
   */
  #updateSectionFromCache(html) {
    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdateEvent(null, this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
          newProduct: null,
        })
      );
    }
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const selection = this.announceProductSelect();

    fetch(requestUrl, { signal: this.#fetchController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');

        selection.resolve({ variantId: String(this.pendingVariantId ?? '') });

        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(null, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct: null,
            })
          );
        }
      })
      .catch((error) => {
        selection.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

class ProductCardCompact extends ProductComponent {
  /** @type {AbortController | null} */
  #fetchController = null;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Abort any pending fetch
    this.#fetchController?.abort();
    this.#fetchController = null;
  }

  variantChanged(event) {
    event.preventDefault();
    event.stopPropagation();
    const variantId = event.target.value;
    if (!variantId) return;
    const productFormComponent = this.querySelector('product-form-component');
    if (!productFormComponent) return;

    const variantIdInput = productFormComponent.refs?.variantId;
    if (variantIdInput) {
      variantIdInput.value = variantId;
    }
    const productUrl = event.target.dataset?.productUrl?.split('?')[0];
    if (!productUrl) return;
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'section-rendering-product-card-compact-partial');

    const requestUrl = url.href;

    // Use parent's fetch method
    this.fetchUpdatedSection(requestUrl);
  }


  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   */
  fetchUpdatedSection(requestUrl) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    fetch(requestUrl, { signal: this.#fetchController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const productCardElement = html.querySelector('product-card-compact');
        if (!productCardElement) return;
        const newProductCardElement = productCardElement.cloneNode(true);
        this.updateProductCardImage(newProductCardElement);
        this.updateProductCardPrice(newProductCardElement);
        this.updateProductCardLink(newProductCardElement);
        this.updateProductFormComponent(newProductCardElement);
        this.updateBackInStockButton(newProductCardElement);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  updateProductCardLink(html) {
    const anchorElement = html.querySelector('.product-card-compact__link');
    const currentAnchorElements = this.querySelectorAll('.product-card-compact__link');
    if (!anchorElement || !currentAnchorElements) return;

    const productUrl = anchorElement.href;
    if (!productUrl) return;
    currentAnchorElements.forEach(element => {
      element.href = productUrl;
    });
  }

  updateProductCardImage(html) {
    const newProductCardImage = html.querySelector('.product-card-compact__image');
    if (newProductCardImage) {
      morph(this.querySelector('.product-card-compact__image'), newProductCardImage);
    }
  }

  updateProductCardPrice(html) {
    const newProductCardPrice = html.querySelector('.product-card__price');
    if (newProductCardPrice) {
      morph(this.querySelector('.product-card__price'), newProductCardPrice);
    }
  }

  updateProductFormComponent(html) {
    const isAvailable = html.getAttribute('data-available') === 'true';
    const productFormComponent = this.querySelector('product-form-component');
    if (isAvailable) {
      productFormComponent.classList.remove('show-back-in-stock');
    } else {
      productFormComponent.classList.add('show-back-in-stock');
    }
  }

  updateBackInStockButton(html) {
    const anchorElement = html.querySelector('.product-card-compact__link');
    if (!(anchorElement instanceof HTMLAnchorElement)) return;
    const productUrl = anchorElement.href;
    if (!productUrl) return;
    const backInStockButton = this.querySelector('back-in-stock-component');
    if (backInStockButton) {
      backInStockButton.setAttribute('data-product-url', productUrl);
    }
  }

  get variantSelector() {
    return this.refs.variantSelector;
  }

  /**
 * Gets product card link element.
 * @returns {HTMLAnchorElement} Product card link
 */
  getProductCardLink() {
    return this.refs.productCardLink;
  }
}

if (!customElements.get('product-card-compact')) {
  customElements.define('product-card-compact', ProductCardCompact);
}